// The entity model. Entity *kinds* are declared here as data so views, forms,
// the command bar, and mention search are all driven by one registry — adding a
// field is a one-line change, and a new kind is a new entry plus a route.
//
// The v2 shape (see planning/apps/crum/v2.md): four kinds, and labels where
// earlier versions would have reached for another table.
//
//   project  every piece of work, labelled by `kind` (demo / artifact / …)
//   deal     one sale, renewal or contract — idea through signed, delivery, delivered.
//            A multi-year relationship is a CHAIN of these (see `renews`), not a
//            separate container: once a deal can sit in `delivery` for a year and owe
//            its own deliverables, a support contract just is a deal.
//   org      any party. What they are TO us (client, lead, partner) is not a field:
//            it was a hardcoded taxonomy that went stale and told you less than the
//            deals on the row already do. If it comes back it comes back as a tag.
//   person   a human, linked to orgs
//
// Entries (the one timeline item), comments, showings (project → org),
// resources, and support edges (deal ↔ project) are separate record types
// handled by the reducer directly.

export type EntityKind = 'project' | 'artifact' | 'deal' | 'org' | 'person'

export type FieldType =
  | 'text'
  | 'rich'
  | 'number'
  | 'email'
  | 'url'
  /** several URLs, one per line, in one text field */
  | 'links'
  | 'ref'
  | 'date'
  | 'select'

/** Where a `select` field gets its options from. Config-backed lists are
 *  user-editable; fixed lists are part of the app's vocabulary. */
export type OptionSource =
  | 'projectKind'
  | 'artifactKind'
  | 'orgState'
  | 'visibility'
  | 'consent'
  | 'access'

export interface FieldDef {
  key: string
  label: string
  type: FieldType
  /** the field that titles the record (shown as its name everywhere) */
  title?: boolean
  /** for type 'ref': which kind this points at */
  refKind?: EntityKind
  /** for type 'select': where the options come from */
  options?: OptionSource
  placeholder?: string
  /** hide from the generic field grid — the detail page renders it specially */
  detail?: boolean
}

/** A many-to-many relationship shown on the detail page, backed by bidirectional links. */
export interface RelationDef {
  kind: EntityKind
  label: string
}

export interface KindDef {
  kind: EntityKind
  /** id prefix, e.g. 'per' → per_01J… */
  prefix: string
  /** singular / plural display labels */
  singular: string
  plural: string
  /** lucide-react icon name */
  icon: string
  /** hash route segment, e.g. 'people' */
  route: string
  /**
   * Which half of the app it belongs to: money or work. This is the grammar — it
   * decides things like which side of a rollup a number lands on.
   */
  hue: 'money' | 'work'
  /**
   * The kind's own colour, from PALETTE. Deals, orgs and people all used to draw in
   * ember because they're all "money side", which made three of the four kinds
   * indistinguishable in a graph, a legend, or a list of mentions. The family still
   * means something; the identity is now per kind.
   *
   * Spread deliberately: rust, teal, violet, gold. The one adjacent pair (deal rust /
   * person gold) is the pair that co-occurs least.
   */
  accent: string
  fields: FieldDef[]
  /** many-to-many relations (via links) editable from this kind's detail page */
  relations?: RelationDef[]
}

export const KINDS: Record<EntityKind, KindDef> = {
  project: {
    kind: 'project',
    prefix: 'prj',
    // A project is an ongoing effort: it has a board, deals and a timeline, and it
    // does not finish just because something shipped out of it. An artifact is a
    // separate record type (below) for the things a project produces.
    //
    // Two earlier attempts were wrong in opposite directions. Calling this type
    // "Artifact" made a live project a thing you made, which it is not. Calling it
    // "Work" and splitting artifacts off as a *lens* was worse: the tab bar then
    // named something the model did not have, so the words and the records
    // disagreed. Tabs name types.
    singular: 'Project',
    plural: 'Projects',
    icon: 'Boxes',
    route: 'work',
    hue: 'work',
    accent: 'marine',
    fields: [
      { key: 'name', label: 'Name', type: 'text', title: true, placeholder: 'Project name' },
      { key: 'kind', label: 'Kind', type: 'select', options: 'projectKind' },
      // Restored 2026-09-17, after a week without them. The argument for removing
      // these was that a URL is a thing you can open and a thing you can open is an
      // artifact — true of a demo, and wrong about everything else. A repo, a shared
      // drive, a call recording, a spec: a project accumulates addresses that are
      // not deliveries, and forcing each one to become "a thing we built" to have
      // somewhere to live is a heavier model, not a cleaner one.
      //
      // So all three carry links, each meaning something slightly different: a deal's
      // are the paperwork, an artifact's is the thing itself, a project's are its
      // context. An artifact stays what its name says — a thing we made.
      { key: 'links', label: 'Links', type: 'links', placeholder: 'https://' },
      { key: 'owner', label: 'Led by', type: 'ref', refKind: 'person' },
      { key: 'start', label: 'Start', type: 'date' },
      { key: 'end', label: 'End', type: 'date' },
      // ONE prose field, called the same thing on every kind. This used to be a
      // `summary` plus a `context` (which is where @mentions went, so it quietly
      // became the important one); two boxes for "what is this" is one too many.
      // Legacy `summary`/`context` are still read — see descriptionOf().
      { key: 'description', label: 'Description', type: 'rich' },
      // Held back by default. A project may exist to sell the client on something
      // they have not agreed to yet, and the client surface is a filter over the
      // team's own log — so the safe direction is the one where a record has to be
      // promoted to travel, never the one where it has to be remembered.
      { key: 'visibility', label: 'Who sees it', type: 'select', options: 'visibility' },
    ],
    // A project reaches its client through the deal that pays for it, which is
    // right when there IS a deal — and leaves internal work, or anything before a
    // contract, belonging to nobody. Since the client is the spine of the app, a
    // project has to be able to say whose it is on its own. `orgsForProject` reads
    // this link first and the deals second, so an attached deal still answers it
    // without anyone linking twice.
    relations: [{ kind: 'org', label: 'Client' }],
  },
  deal: {
    kind: 'deal',
    prefix: 'deal',
    singular: 'Deal',
    plural: 'Deals',
    icon: 'Handshake',
    route: 'deals',
    hue: 'money',
    accent: 'ember',
    // Orgs are a RELATION, not a field: a joint deal has several parties, and one
    // multi-select in one place beats a "signer" field plus an "also involved" list.
    // The legacy single `org` field is still read (orgsForDeal) so old logs keep their
    // counterparty.
    relations: [{ kind: 'org', label: 'Orgs' }],
    fields: [
      { key: 'title', label: 'Title', type: 'text', title: true, placeholder: 'Deal name' },
      // A renewal points at the deal it renews, which is all a multi-year
      // relationship needs: the chain has a head, and lifetime value is a walk.
      { key: 'renews', label: 'Renews', type: 'ref', refKind: 'deal' },
      { key: 'value', label: 'Value', type: 'number', placeholder: '0' },
      { key: 'expectedClose', label: 'Expected close', type: 'date' },
      // The contract, once there is one. `signedAt` is stamped automatically the first
      // time a deal reaches a committed stage; both are editable, because the date you
      // moved the record is not always the date on the paper.
      { key: 'signedAt', label: 'Signed', type: 'date' },
      { key: 'endsAt', label: 'Ends', type: 'date' },
      // Where the paperwork lives. An app can't take a PDF into its own data folder (the
      // file API is text, and the folder is the app's, not the user's corpus), so this is
      // a link to wherever the signed copy actually lives.
      { key: 'links', label: 'Links', type: 'links', placeholder: 'https://' },
      { key: 'description', label: 'Description', type: 'rich' },
    ],
  },
  /**
   * A thing a project produced: a demo, a document, a dataset, a service you now
   * run. Its own record type rather than a state of a project, because the two are
   * genuinely different shapes — a project has a board and a pipeline and never
   * really finishes; an artifact has a date, a link, and exists.
   *
   * One project produces many artifacts over its life, which is the relationship a
   * single record could not express at all.
   */
  artifact: {
    kind: 'artifact',
    prefix: 'art',
    singular: 'Artifact',
    plural: 'Artifacts',
    icon: 'Layers',
    route: 'artifacts',
    hue: 'work',
    accent: 'brass',
    fields: [
      { key: 'name', label: 'Name', type: 'text', title: true, placeholder: 'What it is' },
      { key: 'kind', label: 'Kind', type: 'select', options: 'artifactKind' },
      // The date it landed. An artifact without one is a draft, not a delivery.
      { key: 'delivered', label: 'Delivered', type: 'date' },
      { key: 'links', label: 'Links', type: 'links', placeholder: 'https://' },
      { key: 'description', label: 'Description', type: 'rich' },
      // Same rule as a project, and the reason files are artifacts rather than a
      // record type of their own: a signed contract and an internal cut of a
      // transcript are both "a thing with a link", and only one of them travels.
      { key: 'visibility', label: 'Who sees it', type: 'select', options: 'visibility' },
    ],
    // Same reason as a project: an artifact produced for a client with no deal, or
    // before one, had no way to say so. `orgsForArtifact` already read a direct org
    // link; nothing declared the relation, so no UI ever offered it.
    relations: [
      { kind: 'project', label: 'Came out of' },
      { kind: 'org', label: 'Client' },
    ],
  },
  org: {
    kind: 'org',
    prefix: 'org',
    // The word everywhere else in the app, so the tab, the eyebrow and ⌘K stop
    // disagreeing. The RECORD is still an org: the id and the event vocabulary are
    // `org`, and a non-client — a standards body, a funder — is the same record
    // with no deal on it. If those ever get numerous enough that "Client" reads
    // wrong on their page, the label moves to a view and this goes back.
    singular: 'Client',
    plural: 'Clients',
    icon: 'Building2',
    route: 'orgs',
    hue: 'money',
    accent: 'violet',
    fields: [
      { key: 'name', label: 'Name', type: 'text', title: true, placeholder: 'Organization name' },
      // Unset reads as Client, because that is what an org nobody has marked is.
      // The one worth setting is the org that is us.
      { key: 'website', label: 'Website', type: 'url', placeholder: 'https://' },
      { key: 'location', label: 'Location', type: 'text' },
      { key: 'description', label: 'Description', type: 'rich' },
      /**
       * Whether this org may be NAMED on another client's page.
       *
       * On the org, not on each connection, because that is where the decision
       * actually lives: a client agrees once to being acknowledged, not separately
       * for every piece of work. Before they agree their node still appears on
       * someone else's web — as "another preprint server" — because the shape of
       * the network is not the private part, only the labels are
       * (positioning.md §5).
       */
      { key: 'naming', label: 'May be named', type: 'select', options: 'consent' },
      { key: 'namingAt', label: 'Agreed on', type: 'date', detail: true },
    ],
    relations: [{ kind: 'person', label: 'People' }],
  },
  person: {
    kind: 'person',
    prefix: 'per',
    singular: 'Person',
    plural: 'People',
    icon: 'User',
    route: 'people',
    hue: 'money',
    accent: 'brass',
    fields: [
      { key: 'name', label: 'Name', type: 'text', title: true, placeholder: 'Full name' },
      { key: 'role', label: 'Role', type: 'text', placeholder: 'Title or role' },
      { key: 'email', label: 'Email', type: 'email', placeholder: 'name@example.com' },
      { key: 'phone', label: 'Phone', type: 'text' },
      // Only meaningful for people at the home org; on a client contact it is inert,
      // because the domain gate and the home-org link both have to pass first.
      { key: 'access', label: 'Access', type: 'select', options: 'access' },
      { key: 'description', label: 'Description', type: 'rich' },
    ],
    relations: [{ kind: 'org', label: 'Organizations' }],
  },
}

export const KIND_LIST: KindDef[] = [
  KINDS.project,
  KINDS.artifact,
  KINDS.deal,
  KINDS.org,
  KINDS.person,
]

export function kindOfId(id: string): EntityKind | null {
  const p = id.slice(0, id.indexOf('_'))
  for (const k of KIND_LIST) if (k.prefix === p) return k.kind
  return null
}

export function titleField(kind: EntityKind): string {
  return KINDS[kind].fields.find((f) => f.title)?.key ?? 'name'
}

/** Every rich/text field whose body can carry @mentions, per kind. Used for
 *  backlinks: "which records point at me". */
export function mentionFields(kind: EntityKind): string[] {
  const own = KINDS[kind].fields.filter((f) => f.type === 'rich').map((f) => f.key)
  // `summary` and `context` are where work prose used to live, and existing logs have
  // @mentions in both. Reading them keeps every old edge in the graph.
  return kind === 'project' ? [...own, 'summary', 'context'] : own
}

/**
 * The prose for a record, whatever key it was written under. New writes go to
 * `description`; `context` then `summary` (work) and `about` (org, person) are the
 * fields it replaced.
 */
export const DESCRIPTION_KEYS = ['description', 'context', 'summary', 'about'] as const

/** One link on a record: a URL, and the name you gave it if you gave it one. */
export interface RecordLink {
  label: string
  url: string
}

/**
 * The links on a record, one per line. A line is either a bare URL or a markdown link,
 * `[final signed copy](https://…)` — markdown because it's the convention everyone
 * already reads, it survives being edited by hand or by an agent, and the app already
 * uses the same shape for @mention tokens. `url` is the pre-1.0 single-link key.
 */
export function linksOf(fields: Record<string, unknown>): RecordLink[] {
  const raw = [fields.links, fields.url].find((v) => typeof v === 'string' && v.trim())
  if (typeof raw !== 'string') return []
  return raw
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const md = /^\[([^\]]*)\]\((.+)\)$/.exec(line)
      return md
        ? { label: (md[1] ?? '').trim(), url: (md[2] ?? '').trim() }
        : { label: '', url: line }
    })
    .filter((l) => !!l.url)
}

/** Back to one-per-line text. A link with no label stays a bare URL. */
export function linksToText(links: RecordLink[]): string {
  return links
    .filter((l) => l.url.trim())
    .map((l) => (l.label.trim() ? `[${l.label.trim()}](${l.url.trim()})` : l.url.trim()))
    .join('\n')
}

/**
 * What to show for a link: the label you gave it, else the readable part of the URL
 * (host and path, no scheme, no `www.`), truncated to fit one line.
 */
export function linkLabel(link: RecordLink, max = 30): string {
  const label = link.label.trim()
  if (label) return label.length > max ? `${label.slice(0, max - 1)}…` : label
  const bare = link.url
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/$/, '')
  return bare.length > max ? `${bare.slice(0, max - 1)}…` : bare
}

// ── vocabularies ─────────────────────────────────────────────────────────────

/** A colored label option. `color` keys into PALETTE. */
export interface LabelDef {
  key: string
  label: string
  color: string
  /** Plural form for list headings and the rail. Optional because English is not
   *  regular — "Infrastructure" and "Research" don't take an -s — so we ask for
   *  the form rather than guessing at it. */
  plural?: string
}

/** The plural of a label, falling back to the label itself. */
export function pluralOf(def: LabelDef | undefined): string {
  return def ? (def.plural ?? def.label) : ''
}

// Work has no state field. It used to carry `available | inactive` ("set aside"), which
// was the same species as the `active` flag before it: a hand-set boolean about currency
// that nobody maintains, whose staleness is invisible. What's live is said by the deals
// and the dates; `archived` still covers "I don't want to see this at all". Old logs keep
// their `state` value and nothing reads it.

// There was a read-time alias here mapping the old `deliverable` spelling forward
// to `artifact`. It outlived its meaning: artifact is now a record type, not a
// project kind, so the alias resolved to a key no project kind list contains and
// those records came out with a blank kind. Moving them is the migration's job now
// (scripts/migrate-artifacts.mjs), not the read path's.

/**
 * What an org IS to us, which is exactly one bit: ours, or someone else's.
 *
 * `active`/`inactive` used to live here and is gone, for the reason crum's README
 * already records about the `client` field it removed: a status you maintain by
 * hand goes stale, and this one was derivable anyway. Whether a client is active is
 * told by their deals, their running projects and when you last spoke — all of
 * which are on the page already and none of which anyone has to remember to update.
 *
 * What is NOT derivable is which org is us, and the client-as-spine model needs it:
 * it is what makes "people on our side" a real list rather than a guess. That does
 * not move the way a relationship does, so it does not rot.
 *
 * Legacy `active`/`inactive` values simply match no option and read as unset.
 */
/**
 * The same three levels groups and board rows use, as a pickable field. `internal` is
 * the empty value, so a record nobody has promoted is held back.
 *
 * On a project or artifact this one field answers BOTH questions there are, which is
 * why the labels name an audience rather than say yes or no: whether the client whose
 * work it is sees it on their own page, and how it appears in another client's web.
 * Connections used to carry their own copy of this, one per edge; they are derived
 * now and take the weaker of the two records they join (`reducer.ts`, `Connection`).
 */
export const VISIBILITY_LABELS: LabelDef[] = [
  { key: 'internal', label: 'Just us', color: 'grey' },
  { key: 'client', label: 'This client', color: 'green' },
  { key: 'acknowledgeable', label: 'Nameable anywhere', color: 'teal' },
]

/** Whether an org has agreed to be named on another client's page. Unset means no,
 *  so nobody is named by nobody having asked. */
export const CONSENT_LABELS: LabelDef[] = [
  { key: 'none', label: 'Not asked', color: 'grey' },
  { key: 'requested', label: 'Asked', color: 'amber' },
  { key: 'approved', label: 'Agreed', color: 'green' },
  { key: 'declined', label: 'Declined', color: 'red' },
]

/**
 * Client, or us.
 *
 * **Not an editable field on an org.** It decides who can sign in (see
 * worker/members.ts), and a deployment has exactly one org that is us, neither of
 * which a per-record dropdown can express: two people could mark two orgs in the
 * same minute and nothing would object. So it is written from one place —
 * Settings → Who can sign in — as a single choice, which makes "exactly one" true
 * by construction rather than by hope. An org's own page shows it as a badge.
 */
export const ORG_STATES: LabelDef[] = [
  { key: 'client', label: 'Client', color: 'grey' },
  { key: 'internal', label: 'Internal', color: 'teal' },
]

/**
 * Whether a piece of work is currently earning — DERIVED from the deals attached to
 * it, never stored. This is what "active" was always trying and failing to say.
 */
export type WorkStatus = 'delivery' | 'pipeline' | 'done' | 'lost' | 'none'

export const WORK_STATUSES: Record<WorkStatus, LabelDef> = {
  delivery: { key: 'delivery', label: 'In delivery', color: 'green' },
  pipeline: { key: 'pipeline', label: 'In pipeline', color: 'ember' },
  done: { key: 'done', label: 'Delivered', color: 'marine' },
  // A demo whose only deal was lost is its own fact, and an interesting one: it was
  // built, it was shown, it didn't land. Folding that into "No deal" threw it away.
  lost: { key: 'lost', label: 'Not won', color: 'red' },
  none: { key: 'none', label: 'No deal', color: 'grey' },
}

/** One timeline item, on any subject. Replaces v1's note and Atlas's log entry. */
export type EntryKind = 'note' | 'meeting' | 'update' | 'decision'

export const ENTRY_KINDS: { key: EntryKind; label: string; verb: string; icon: string }[] = [
  { key: 'note', label: 'Note', verb: 'left a note', icon: 'MessageSquare' },
  // A call and a meeting are the same thing: you spoke to someone and here's what was
  // said. Splitting them asked for a decision at the moment of writing that nothing
  // downstream ever read. Old `call` entries read as meetings.
  { key: 'meeting', label: 'Meeting', verb: 'logged a meeting', icon: 'Users' },
  { key: 'update', label: 'Update', verb: 'posted an update', icon: 'Activity' },
  { key: 'decision', label: 'Decision', verb: 'recorded a decision', icon: 'Check' },
]

const ENTRY_KIND_ALIASES: Record<string, EntryKind> = { call: 'meeting' }

export function entryKind(key: string): (typeof ENTRY_KINDS)[number] {
  const k = ENTRY_KIND_ALIASES[key] ?? key
  return ENTRY_KINDS.find((x) => x.key === k) ?? ENTRY_KINDS[0]!
}

/**
 * Deal ↔ work edges. These used to carry a role (`supports` / `produces`), which
 * split one relation into two labels that nobody read the same way twice — and the
 * work's own `kind` (demo, artifact, infrastructure) already says what it is. The
 * role is still folded off old events so nothing breaks; nothing writes or shows it.
 */

// ── config (stored in v1/config.json) ────────────────────────────────────────

/**
 * A stage's SEMANTIC, which is what every rollup reads — the labels are yours, these
 * are not. Won used to be terminal, which made it impossible to say "signed, and the
 * work is running" or "signed, and we're finished".
 *
 *   open      · in the pipeline. Might not happen.
 *   won       · committed. Signed, delivery not started.
 *   delivery  · committed AND running. This is where the contract lives.
 *   closed    · delivered and wrapped up.
 *   lost      · didn't happen.
 *
 * The split matters because "will this happen" and "is it done" are different
 * questions — the same reason agency tools keep a sales stage and a delivery status
 * as two axes rather than one long ladder.
 */
export type StageKind = 'open' | 'won' | 'delivery' | 'closed' | 'lost'

/** Signed: the money is committed, whether or not the work has started. */
export const COMMITTED_KINDS: StageKind[] = ['won', 'delivery']

export function isCommitted(kind: StageKind | undefined): boolean {
  return kind === 'won' || kind === 'delivery'
}
/**
 * What colour a stage reads as, by what it MEANS rather than where it sits: green
 * for signed money, marine for work that is finished, red for lost, and null for
 * an open stage, which has no single colour — the Deals board shades those along a
 * ramp so the colour itself carries progress, and a caller with no ramp (an aside,
 * a chip) uses its own default.
 */
export function stageTone(kind: StageKind | undefined): string | null {
  if (kind === 'won' || kind === 'delivery') return 'var(--good)'
  if (kind === 'closed') return 'var(--marine)'
  if (kind === 'lost') return 'var(--crit)'
  return null
}

/** Counts toward lifetime revenue: committed or finished. */
export function isBooked(kind: StageKind | undefined): boolean {
  return isCommitted(kind) || kind === 'closed'
}

export interface Stage {
  key: string
  label: string
  kind: StageKind
  /** What this stage MEANS for your team — the entry criteria, in a sentence. Shown
   *  under the stage heading on the Deals page, edited in Settings. A pipeline whose
   *  stages aren't defined is a pipeline everyone reads differently. */
  description?: string
}

/**
 * How far a record reaches. **Three values, not a boolean**, and the third exists
 * from the very first migration even though nothing reads it yet: retrofitting a
 * third state into a true/false is the expensive kind of mistake, and this one is
 * load-bearing for the ecosystem view.
 *
 *   internal        team only
 *   client          appears on this org's own page
 *   acknowledgeable may be NAMED on another org's page — needs that org's consent,
 *                   which is recorded on the org, not here
 */
/**
 * Who on the team can sign in, held on the person rather than in env.
 *
 * The rule the labels describe: a person linked to the org marked **Internal** (the
 * team's own, see ORG_STATES) who has an email address is a member. This field only exists to say otherwise —
 * to promote someone to admin, or to keep a colleague in the People list without
 * giving them a login. Most people never carry it.
 *
 * It is deliberately NOT sufficient on its own. The identity provider authenticates,
 * `MEMBER_ALLOW` says which domains may ever be admitted, and only then does this
 * decide. So a member adding a person record can narrow who gets in but can never
 * widen it past the domain gate an operator set. See worker/auth.ts.
 */
export type Access = 'default' | 'admin' | 'none'

export const ACCESS_LABELS: LabelDef[] = [
  { key: 'default', label: 'Team member', color: 'grey' },
  { key: 'admin', label: 'Admin', color: 'ember' },
  { key: 'none', label: 'No access', color: 'grey' },
]

export function normalizeAccess(v: unknown): Access {
  return v === 'admin' || v === 'none' ? v : 'default'
}

export type Visibility = 'internal' | 'client' | 'acknowledgeable'

export const VISIBILITY: Visibility[] = ['internal', 'client', 'acknowledgeable']

/** Default for everything: a human promotes, nothing leaks by being created. */
export const DEFAULT_VISIBILITY: Visibility = 'internal'

export function asVisibility(v: unknown): Visibility {
  return v === 'client' || v === 'acknowledgeable' ? v : 'internal'
}

/** Reaches the client's own page. `acknowledgeable` implies `client`. */
export function showsToClient(v: Visibility): boolean {
  return v === 'client' || v === 'acknowledgeable'
}

/**
 * The client's own rating of an item, 0–3 — rendered as three bars, green at one,
 * amber at two, red at three. Deliberately not five stars: stars read as a rating
 * of quality rather than a level, and a five-point scale is one nobody applies
 * consistently. The team sorts by this column and never writes it.
 */
export const MAX_PRIORITY = 3

export function asPriority(v: unknown): number {
  const n = typeof v === 'number' ? Math.round(v) : Number.NaN
  return Number.isFinite(n) ? Math.min(Math.max(n, 0), MAX_PRIORITY) : 0
}

/**
 * Item statuses. Deliberately short — a status nobody can define is one everybody
 * reads differently — and deliberately not config-backed yet: these keys are read
 * by the client's seat, so they are a contract before they are a preference.
 *
 * `blocked` is absent on purpose. It is derived from an item's `waitingOn` edges
 * (see `isBlocked`), never stored, because a stale blocked flag on a client's page
 * is worse than no flag at all.
 */
export const ITEM_STATUSES: LabelDef[] = [
  { key: 'next', label: 'Next', color: 'grey' },
  { key: 'in-flight', label: 'In flight', color: 'green' },
  { key: 'in-review', label: 'In review', color: 'ember' },
  { key: 'done', label: 'Done', color: 'marine' },
  { key: 'someday', label: 'Someday', color: 'grey' },
]

export function itemStatus(key: string): LabelDef {
  return labelOf(ITEM_STATUSES, key) ?? { key: key || 'next', label: key || 'Next', color: 'grey' }
}

/**
 * How a due date reads: red inside a week, amber inside a month, quiet beyond.
 * Lives here beside the other colour helpers so the board, the client page and the
 * Due view can never drift on what "soon" looks like.
 */
export function dueTone(due: string): string {
  if (!due) return 'var(--faint)'
  const days = (new Date(`${due}T00:00:00`).getTime() - Date.now()) / 86_400_000
  if (days < 7) return 'var(--crit)'
  if (days < 30) return 'var(--warn)'
  return 'var(--faint)'
}

export interface Config {
  /** what sort of thing an artifact is */
  artifactKinds: LabelDef[]
  /** the deal pipeline, editable: `idea` is where an opportunity starts life,
   *  `won` is where a deal becomes a contract. One record, whole lifecycle. */
  stages: Stage[]
  /** work kinds, user-editable label + plural + color */
  projectKinds: LabelDef[]
  /**
   * Work states are editable too — but only their LABEL and
   * COLOR. The keys are load-bearing: `inactive` decides what drops out of rollups,
   * `won`/`lost` decide what counts as money in the bank. Letting those be renamed
   * would quietly break the views, so Settings edits the wording and locks the key.
   */
  /** tag label → palette color key */
  tagColors: Record<string, string>
}

export const DEFAULT_CONFIG: Config = {
  stages: [
    {
      key: 'idea',
      label: 'Idea',
      kind: 'open',
      description:
        'Something we thought of, not something they asked for. No commitment either way.',
    },
    {
      key: 'lead',
      label: 'Lead',
      kind: 'open',
      description: 'They came to us, or we made contact. Interest is real but unqualified.',
    },
    {
      key: 'qualified',
      label: 'Qualified',
      kind: 'open',
      description: 'We know what they need, roughly what it costs, and that they can decide.',
    },
    {
      key: 'proposal',
      label: 'Proposal',
      kind: 'open',
      description: 'A number is in their hands. The ball is on their side.',
    },
    {
      key: 'negotiation',
      label: 'Negotiation',
      kind: 'open',
      description: 'They want it; we are settling scope, price, or timing.',
    },
    {
      key: 'won',
      label: 'Won',
      kind: 'won',
      description:
        'Signed, work not started. The deal is now a contract with dates and deliverables.',
    },
    {
      key: 'delivery',
      label: 'In delivery',
      kind: 'delivery',
      description: 'Signed and running. The contract timeline below is the live picture.',
    },
    {
      key: 'delivered',
      label: 'Delivered',
      kind: 'closed',
      description:
        'Everything owed has been handed over. Kept, because it is the record of what we did.',
    },
    {
      key: 'lost',
      label: 'Lost',
      kind: 'lost',
      description: 'Not happening. Kept, because knowing why is worth more than a tidy list.',
    },
  ],
  // What SORT of ongoing effort. `demo` and `artifact` (spelled `deliverable` in
  // older logs) used to be here and have moved to the artifact type, where they
  // belong: they named things produced, not efforts under way. A record still
  // carrying one is one the artifact migration has not moved yet.
  projectKinds: [
    { key: 'build', label: 'Build', plural: 'Builds', color: 'marine' },
    { key: 'infrastructure', label: 'Hosting', plural: 'Hosting', color: 'teal' },
    { key: 'research', label: 'Research', plural: 'Research', color: 'violet' },
    { key: 'product', label: 'Product', plural: 'Products', color: 'ember' },
    { key: 'internal', label: 'Internal', plural: 'Internal work', color: 'grey' },
  ],
  /** What sort of thing was produced. An artifact is a record, not a status. */
  artifactKinds: [
    { key: 'demo', label: 'Demo', plural: 'Demos', color: 'marine' },
    { key: 'document', label: 'Document', plural: 'Documents', color: 'brass' },
    { key: 'dataset', label: 'Dataset', plural: 'Datasets', color: 'violet' },
    { key: 'service', label: 'Service', plural: 'Services', color: 'teal' },
    { key: 'other', label: 'Other', plural: 'Other', color: 'grey' },
  ],
  tagColors: {},
}

/** Resolve a `select` field's options against config + the fixed vocabularies. */
export function optionsFor(source: OptionSource, config: Config): LabelDef[] {
  switch (source) {
    case 'projectKind':
      return config.projectKinds
    case 'artifactKind':
      return config.artifactKinds
    case 'orgState':
      return ORG_STATES
    case 'visibility':
      return VISIBILITY_LABELS
    case 'consent':
      return CONSENT_LABELS
    case 'access':
      return ACCESS_LABELS
  }
}

export function labelOf(list: LabelDef[], key: string): LabelDef | undefined {
  return list.find((l) => l.key === key)
}

// ── palette ──────────────────────────────────────────────────────────────────
// Colors are named, and every name maps to a CSS variable so light/dark are one
// definition. Four of them carry the record kinds (see KindDef.accent): ember for
// deals, marine for work, violet for orgs, brass for people. `hue` still says which
// half of the app a kind belongs to; the accent says which kind it is.

export const PALETTE: Record<string, string> = {
  ember: '--ember',
  marine: '--marine',
  brass: '--brass',
  green: '--good',
  amber: '--warn',
  red: '--crit',
  teal: '--c-teal',
  violet: '--c-violet',
  blue: '--c-blue',
  rose: '--c-rose',
  grey: '--muted',
}

export function colorVar(color: string | undefined): string {
  return (color && PALETTE[color]) || '--muted'
}

/** The mention chip class for a kind: one class per accent, so a body full of
 *  mentions is scannable by colour alone. */
export function mentionClass(kind: EntityKind | undefined): string {
  switch (kind) {
    case 'project':
      return 'mention mention-work'
    case 'org':
      return 'mention mention-org'
    case 'person':
      return 'mention mention-person'
    default:
      return 'mention mention-money'
  }
}

/** A kind's own colour as a CSS var name, e.g. `--c-violet` for an org. */
export function accentVar(kind: EntityKind | undefined): string {
  return kind ? colorVar(KINDS[kind].accent) : '--muted'
}

/** Tag colors: an explicit assignment, else a stable hash so an unassigned tag
 *  still reads consistently everywhere. */
export const TAG_PALETTE = [
  'amber',
  'blue',
  'green',
  'violet',
  'rose',
  'teal',
  'ember',
  'marine',
] as const
export type TagColor = (typeof TAG_PALETTE)[number]

export function tagColor(label: string, config: Config): TagColor {
  const explicit = config.tagColors[label]
  if (explicit && (TAG_PALETTE as readonly string[]).includes(explicit)) return explicit as TagColor
  let h = 0
  for (let i = 0; i < label.length; i++) h = (h * 31 + label.charCodeAt(i)) | 0
  return TAG_PALETTE[Math.abs(h) % TAG_PALETTE.length]!
}
